/**
 * Data-source live polling helpers (TASK-230)
 *
 * A data source can sit in a non-terminal status ("Обработка"/"PENDING") while
 * the backend indexes it. Without polling the list only updates on F5. These
 * pure helpers decide whether to keep polling and are unit-testable in
 * isolation from the React Query wiring.
 */
import type { DataSource, DataSourceStatus } from '@/types/dataSource';

/** Statuses that are still "in flight" — a transition is expected, keep polling. */
export const NON_TERMINAL_STATUSES: DataSourceStatus[] = ['PENDING', 'PROCESSING'];

/** Polling cadence (ms) while at least one source is non-terminal. */
export const DATA_SOURCES_POLL_INTERVAL_MS = 4000;

/**
 * True when at least one data source is still processing-like (PENDING /
 * PROCESSING). Safe for undefined input (returns false → polling off).
 */
export function hasProcessingSource(sources: DataSource[] | undefined): boolean {
  return (sources ?? []).some((s) => NON_TERMINAL_STATUSES.includes(s.status));
}

/**
 * True when at least one data source is strictly PENDING (TASK-298).
 *
 * Unlike {@link hasProcessingSource}, this excludes PROCESSING. Uploaded
 * sources (TASK-273) sit in PENDING until a manual reindex is triggered — the
 * watchdog intentionally skips them — so the page surfaces a banner prompting
 * the user to reindex. Safe for undefined input (returns false).
 */
export function hasPendingSource(sources: DataSource[] | undefined): boolean {
  return (sources ?? []).some((s) => s.status === 'PENDING');
}

/**
 * Count of strictly PENDING (unindexed-awaiting-reindex) sources (TASK-298).
 * Used to fill the banner's count. Safe for undefined input (returns 0).
 */
export function countPendingSources(sources: DataSource[] | undefined): number {
  return (sources ?? []).filter((s) => s.status === 'PENDING').length;
}
